import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import ar from "../i18n/ar";
import en from "../i18n/en";

const translations = { ar, en };
const STORAGE_KEY = "app_lang";
const DEFAULT_LANG = "ar";

const LanguageContext = createContext( null );

export function LanguageProvider ( { children } )
{
    const [ lang, setLang ] = useState( () =>
    {
        const saved = localStorage.getItem( STORAGE_KEY );
        return translations[ saved ] ? saved : DEFAULT_LANG;
    } );

    // مزامنة اتجاه الصفحة (rtl/ltr) ولغة الـ <html> مع اللغة المختارة
    useEffect( () =>
    {
        localStorage.setItem( STORAGE_KEY, lang );
        document.documentElement.lang = lang;
        document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    }, [ lang ] );

    // t("auth.loginButton") أو t("errors.serverError")
    // بيدعم متغيرات: t("cart.itemsCount", { count: 3 }) مع مفتاح زي "لديك {{count}} عناصر"
    const t = useCallback( ( key, vars ) =>
    {
        const value = key
            .split( "." )
            .reduce( ( obj, part ) => ( obj ? obj[ part ] : undefined ), translations[ lang ] );

        if ( value === undefined )
        {
            console.warn( `[i18n] Missing translation key: "${ key }" for language "${ lang }"` );
            return key;
        }

        if ( vars )
        {
            return Object.entries( vars ).reduce(
                ( str, [ k, v ] ) => str.replace( `{{${ k }}}`, v ),
                value
            );
        }

        return value;
    }, [ lang ] );

    const changeLanguage = useCallback( ( nextLang ) =>
    {
        if ( translations[ nextLang ] ) setLang( nextLang );
    }, [] );

    const toggleLanguage = useCallback( () =>
    {
        setLang( ( prev ) => ( prev === "ar" ? "en" : "ar" ) );
    }, [] );

    const value = useMemo( () => ( {
        lang,
        dir: lang === "ar" ? "rtl" : "ltr",
        t,
        changeLanguage,
        toggleLanguage,
    } ), [ lang, t, changeLanguage, toggleLanguage ] );

    return (
        <LanguageContext.Provider value={ value }>
            { children }
        </LanguageContext.Provider>
    );
}

export function useLanguage ()
{
    const ctx = useContext( LanguageContext );
    if ( !ctx )
    {
        throw new Error( "useLanguage لازم يُستخدم جوه <LanguageProvider>" );
    }
    return ctx;
}
